import React, { useCallback, useLayoutEffect, useState } from 'react'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { FormItemProps, SelectProps } from 'antd'
import { DefaultOptionType, SelectValue } from 'antd/lib/select'
import cl from 'classnames'

import { MultiSelect, Select as SelectElem } from 'Shared/atoms'
import { withTooltip } from 'Shared/HOCs/withTooltip'
import { hasError } from 'Shared/utils/validation'

import { FieldNameRegexType } from 'Core/interfaces'

import FormItem from './FormItem'
import { addMaxLengthToSelectInputWithClassName } from './helpers'
import NotFoundContent from './NotFoundContent'

import styles from './input.module.scss'

const Select = withTooltip(SelectElem)

export type FormSelectProps<FieldsType extends FieldValues> = SelectProps<SelectValue> &
	FormItemProps & {
		label?: string
		wrapperClassName?: string
		multiple?: boolean
		control: Control<FieldsType>
		fieldName: keyof FieldsType | FieldNameRegexType
		onChange?: (value: SelectValue, option: DefaultOptionType[] | DefaultOptionType) => void
		allowSelectTyping?: boolean
		useFullOption?: boolean
		inputClassName?: string
		showTooltip?: boolean
		onBlurSelect?: () => void
		showPlaceholderWithEmptyValue?: boolean
		onSelect?: (value: SelectValue, option?: DefaultOptionType | DefaultOptionType[]) => void
		validate?: boolean
		helpMessage?: BasicString
		isReadOnly?: boolean
		notClear?: boolean
	}

function FormSelect<FieldsType extends FieldValues>({
	extra,
	label,
	multiple,
	wrapperClassName,
	control,
	fieldName,
	onChange,
	inputClassName,
	useFullOption = false,
	showTooltip = false,
	onBlurSelect,
	showPlaceholderWithEmptyValue,
	onSelect,
	validate,
	onDropdownVisibleChange,
	helpMessage,
	...props
}: FormSelectProps<FieldsType>) {
	const [isOpen, setIsOpen] = useState<boolean>(false)

	useLayoutEffect(() => {
		if (props.mode !== 'tags' || !inputClassName || !props.maxTagTextLength) {
			return
		}

		addMaxLengthToSelectInputWithClassName(inputClassName, props.maxTagTextLength.toString())
	}, [inputClassName, props.mode, props.maxTagTextLength])
	const handleSelectWithFullOption = (
		value: SelectValue,
		option: DefaultOptionType | DefaultOptionType[]
	) => {
		onSelect && onSelect(option as SelectValue)
	}

	const handleFocus = useCallback(() => {
		setIsOpen(true)
	}, [])

	const handleBlur = useCallback(() => {
		onBlurSelect && onBlurSelect()
		setIsOpen(false)
	}, [onBlurSelect])

	const handleDropdownVisibleChange = useCallback(
		(visible: boolean) => {
			onDropdownVisibleChange?.(visible)
			setIsOpen(visible)
		},
		[onDropdownVisibleChange]
	)

	return (
		<Controller
			name={fieldName as FieldPath<FieldsType>}
			control={control}
			render={({ field: { onChange, name, ...field }, fieldState: { error } }) => {
				const handleChangeWithFullOption = (
					value: SelectValue,
					option: DefaultOptionType | DefaultOptionType[]
				) => {
					if (props.labelInValue && showPlaceholderWithEmptyValue) {
						const correctValue = value as LabelValue
						correctValue === '' ? onChange(null) : onChange(option)
						return
					}
					onChange(option)
				}

				const shouldUseFullOption = props.labelInValue && useFullOption

				const value = (field?.value?.label === '' ? null : field.value) as SelectValue

				return (
					<FormItem
						className={cl(styles.inputWrapper, styles.select, wrapperClassName, {
							[styles.tagStyle]: props.mode === 'tags'
						})}
						label={label}
						error={hasError(error)}
						colon={false}
						name={name}
						extra={extra}
						validate={validate}
						helpMessage={helpMessage}
					>
						{!!multiple ? (
							<MultiSelect
								{...props}
								{...field}
								onChange={onChange}
								dataTestId={`qaa-${name}`}
							/>
						) : (
							<Select
								{...props}
								{...field}
								value={value}
								onDropdownVisibleChange={handleDropdownVisibleChange}
								showArrow
								dataTestId={`qaa-${name}`}
								notFoundContent={
									<NotFoundContent
										isLoading={props.loading}
										isSearch={props.showSearch}
									/>
								}
								onFocus={handleFocus}
								onBlur={handleBlur}
								open={isOpen}
								allowClear={
									!props.notClear ?? (props.allowClear || props.showSearch)
								}
								className={cl(props.className, inputClassName)}
								onChange={
									shouldUseFullOption ? handleChangeWithFullOption : onChange
								}
								onSelect={
									shouldUseFullOption ? handleSelectWithFullOption : onSelect
								}
								showTooltip={!field.value && showTooltip}
							/>
						)}
					</FormItem>
				)
			}}
		/>
	)
}

export default FormSelect
